import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Users, Target, Radio, MapPin, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Loader } from "@/components/app/Loader";

const MOOD_BADGE = {
  positive: "bg-pos-bg text-pos-text",
  neutral: "bg-neu-bg text-neu-text",
  negative: "bg-neg-bg text-neg-text",
};

const SENT_DOT = { positive: "bg-pos-text", neutral: "bg-neu-text", negative: "bg-neg-text" };

/** Drill-down for one county: voter mix, GOTV precinct board, local coverage. */
export function CountyDetailSheet({ county, onClose }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["countyDetail", county],
    queryFn: () => api.get(`/audience/geo/county/${encodeURIComponent(county)}`).then((r) => r.data),
    enabled: !!county,
    retry: false,
  });

  return (
    <Sheet open={!!county} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-[520px]" data-testid="county-detail-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 font-heading text-xl">
            <MapPin className="h-5 w-5 text-brand-red" /> {county} County
          </SheetTitle>
          <SheetDescription>
            {data ? `${data.voters.total.toLocaleString()} voters on file · ${data.sentiment.voter_share ?? 0}% of the district` : "Loading county detail…"}
          </SheetDescription>
        </SheetHeader>

        {isLoading && <div className="mt-6"><Loader label="Pulling county detail…" /></div>}
        {isError && <p className="mt-6 text-sm text-brand-mute" data-testid="county-detail-error">No voters on file for this county yet.</p>}

        {data && (
          <div className="mt-5 space-y-5">
            {/* Sentiment */}
            <div className="rounded-xl border border-border p-4">
              <div className="flex items-center justify-between">
                <h4 className="font-heading text-sm font-bold text-brand-navy">Sentiment</h4>
                {data.sentiment.mood && (
                  <Badge className={MOOD_BADGE[data.sentiment.mood]} data-testid="county-mood">
                    {data.sentiment.mood} · {data.sentiment.score}
                  </Badge>
                )}
              </div>
              <p className="mt-2 text-sm text-brand-ink">
                {data.sentiment.top_issues?.length ? data.sentiment.top_issues.join(" · ") : "No issue signal yet"}
              </p>
              <p className="mt-1 text-xs text-brand-mute">
                {data.sentiment.signal_count ?? 0} local {data.sentiment.signal_count === 1 ? "signal" : "signals"} in the feed
              </p>
            </div>

            {/* Voter mix */}
            <div className="rounded-xl border border-border p-4" data-testid="county-voter-mix">
              <h4 className="flex items-center gap-1.5 font-heading text-sm font-bold text-brand-navy">
                <Users className="h-4 w-4" /> Voter mix
              </h4>
              <div className="mt-3 grid grid-cols-3 gap-3 text-center">
                <Stat label="Avg support" value={data.voters.avg_support} />
                <Stat label="Avg turnout" value={data.voters.avg_turnout} />
                <Stat label="Avg age" value={data.voters.avg_age} />
              </div>
              <div className="mt-3 space-y-1.5">
                {data.parties.map((p) => (
                  <div key={p.party} className="flex items-center gap-2 text-xs">
                    <span className="w-24 shrink-0 truncate text-brand-mute">{p.party}</span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-brand-navy" style={{ width: `${p.share}%` }} />
                    </div>
                    <span className="w-16 shrink-0 text-right font-medium text-brand-ink">{p.share}%</span>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-brand-mute">
                Top communities: {data.cities.slice(0, 4).map((c) => `${c.city} (${c.count.toLocaleString()})`).join(" · ")}
              </p>
            </div>

            {/* GOTV precincts */}
            <div className="rounded-xl border border-border p-4" data-testid="county-precincts">
              <div className="flex items-center justify-between">
                <h4 className="flex items-center gap-1.5 font-heading text-sm font-bold text-brand-navy">
                  <Target className="h-4 w-4" /> GOTV precincts
                </h4>
                <Button asChild size="sm" variant="outline">
                  <Link to="/gotv" data-testid="county-gotv-link">Open GOTV board</Link>
                </Button>
              </div>
              <p className="mt-2 text-xs text-brand-mute">
                {data.gotv.contacted.toLocaleString()} of {data.gotv.universe.toLocaleString()} chase targets contacted ({data.gotv.coverage}%)
              </p>
              <Progress value={data.gotv.coverage} className="mt-2 h-2" />
              <div className="mt-3 space-y-2">
                {data.precincts.map((p) => (
                  <div key={p.precinct} className="flex items-center gap-2 text-xs" data-testid={`county-precinct-${p.precinct}`}>
                    <span className="w-16 shrink-0 font-medium text-brand-ink">{p.precinct}</span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-brand-red" style={{ width: `${p.coverage}%` }} />
                    </div>
                    <span className="w-24 shrink-0 text-right text-brand-mute">{p.contacted}/{p.universe}</span>
                  </div>
                ))}
                {!data.precincts.length && <p className="text-xs text-brand-mute">No chase targets in this county.</p>}
              </div>
            </div>

            {/* Local coverage */}
            <div className="rounded-xl border border-border p-4" data-testid="county-coverage">
              <div className="flex items-center justify-between">
                <h4 className="flex items-center gap-1.5 font-heading text-sm font-bold text-brand-navy">
                  <Radio className="h-4 w-4" /> Local coverage
                </h4>
                <Button asChild size="sm" variant="outline">
                  <Link to="/feed" data-testid="county-feed-link">Open feed</Link>
                </Button>
              </div>
              <div className="mt-3 space-y-3">
                {!data.coverage.length && <p className="text-xs text-brand-mute">No coverage mentioning this county yet.</p>}
                {data.coverage.map((c) => (
                  <div key={c.id} className="border-b border-border pb-2.5 last:border-0 last:pb-0" data-testid={`county-coverage-${c.id}`}>
                    <div className="flex items-start gap-2">
                      <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${SENT_DOT[c.sentiment] || "bg-slate-300"}`} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium leading-snug text-brand-ink">{c.title}</p>
                        <p className="mt-0.5 text-xs text-brand-mute">
                          {c.author || c.source_type} · {c.published_day || timeAgo(c.timestamp)}
                        </p>
                      </div>
                      {c.url && (
                        <a href={c.url} target="_blank" rel="noreferrer" className="ml-auto shrink-0 text-brand-mute hover:text-brand-red">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 p-2.5">
      <p className="font-heading text-xl font-extrabold text-brand-ink">{value}</p>
      <p className="text-[0.65rem] uppercase tracking-wider text-brand-mute">{label}</p>
    </div>
  );
}
